import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Grid,
  Typography,
  Card,
  CardContent,
  Skeleton,
  FormControl,
  FormLabel,
  Box,
  CardHeader,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TwitterPicker } from 'react-color';

import { AccessTime } from '@mui/icons-material';
import { primary } from '../../../../../../../utils/brandColors';
import colors from '../../../../../../../utils/colors';
import TagsAutocomplete from '../../../../../../../components/Autocompletes/Tags';

import styles from './styles';

const useStyles = makeStyles(styles);

export const GeneralFormComponent = (props) => {
  const classes = useStyles();
  const {
    eventTemplate,
    handleNameChange,
    handleChangeTags,
    handleDescriptionChange,
    isLoading,
    handleChangeEventDataDefaultValues,
    tags,
  } = props;

  const label = (
    <Grid item xs={12} md={3} lg={2}>
      <Typography variant="subtitle2">General Calendare Type Details</Typography>
      <Typography variant="caption" gutterBottom>
        Define a calendar type by giving it a name, description, and optional tags
      </Typography>
    </Grid>
  );

  if (isLoading) {
    return (
      <Grid container spacing={4}>
        {label}
        <Grid item xs={12} md={9} lg={10} container className={classes.section}>
          <Card className={classes.card}>
            <CardContent>
              <Skeleton className={classes.nameField} />
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={4}>
      {label}
      <Grid item xs={12} md={9} lg={10} container className={classes.section}>
        <Card className={classes.card}>
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <TextField
                  error={!eventTemplate.validation.name.isValid}
                  margin="normal"
                  fullWidth
                  required
                  variant="outlined"
                  id="name"
                  label="Name"
                  value={eventTemplate.name}
                  autoFocus
                  onChange={handleNameChange}
                  className={classes.nameField}
                  helperText={
                    !eventTemplate.validation.name.isValid
                      && eventTemplate.validation.name.message
                  }
                />

                <TextField
                  error={!eventTemplate.validation.description.isValid}
                  margin="normal"
                  fullWidth
                  variant="outlined"
                  id="description"
                  label="Description"
                  value={eventTemplate.description}
                  onChange={handleDescriptionChange}
                  multiline
                  rows={3}
                  helperText={
                    !eventTemplate.validation.description.isValid
                    && eventTemplate.validation.description.message
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  margin="normal"
                  fullWidth
                  variant="outlined"
                >
                  <FormLabel id="board-display-radio-buttons-group-label">
                    <Box sx={{ display: 'flex' }}>
                      <Box>Calendar Display Color</Box>
                      <div
                        className="circle"
                        style={{
                          marginLeft: '0.5rem',
                          height: '25px',
                          width: '25px',
                          borderRadius: '25px',
                          backgroundColor:
                        (eventTemplate.eventDataDefaultValues && eventTemplate.eventDataDefaultValues.calendarDisplayColor) || primary.main,
                        }}
                        role="presentation"
                      />
                    </Box>
                  </FormLabel>
                  <TwitterPicker
                    colors={colors}
                    color={(eventTemplate.eventDataDefaultValues && eventTemplate.eventDataDefaultValues.calendarDisplayColor) || primary.main}
                    onChange={(e) => {
                      const calendarDisplayColor = e.hex;
                      handleChangeEventDataDefaultValues({
                        ...eventTemplate.eventDataDefaultValues,
                        calendarDisplayColor,
                      });
                    }}
                    direction="vertical"
                  />
                  <Box
                    sx={{
                      mx: 2,
                      pb: 1,
                      fontSize: 14,
                      fontWeight: 300,
                      zIndex: 10000,
                      position: 'relative',
                    }}
                  >
                    <Typography fontWeight={300} component="div">Pick your own color on the</Typography>
                    <a href="https://g.co/kgs/rxPsQ5R" target="blank">Google Hex Color Picker</a>
                  </Box>
                </FormControl>
                <CardHeader
                  sx={{ pb: 0 }}
                  title={(
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mr: 1,
                        }}
                      ><AccessTime color="primary" sx={{ mr: 1 }} />Default Duration
                      </Box>
                      <FormControl>
                        <InputLabel id="demo-simple-select-label">Duration</InputLabel>
                        <Select
                          size="small"
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={(eventTemplate.eventDataDefaultValues && eventTemplate.eventDataDefaultValues.defaultDuration
                               && parseInt(eventTemplate.eventDataDefaultValues.defaultDuration, 10)) || 1800000}
                          label="Duration"
                          onChange={(e) => {
                            handleChangeEventDataDefaultValues({
                              ...eventTemplate.eventDataDefaultValues,
                              defaultDuration: e.target.value,
                            });
                          }}
                        >
                          <MenuItem value="900000">15 minutes</MenuItem>
                          <MenuItem value="1800000">30 minutes</MenuItem>
                          <MenuItem value="2700000">45 minutes</MenuItem>
                          <MenuItem value="3600000">1 hour</MenuItem>
                          <MenuItem value="4500000">1 hour 15 minutes</MenuItem>
                          <MenuItem value="5400000">1 hour 30 minutes</MenuItem>
                          <MenuItem value="6300000">1 hour 45 minutes</MenuItem>
                          <MenuItem value="7200000">2 hours</MenuItem>
                          <MenuItem value="9000000">2 hours 30 minutes</MenuItem>
                          <MenuItem value="10800000">3 hours</MenuItem>
                          <MenuItem value="14400000">4 hours</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  subheader="Set the default duration for this calendar when specifying a time during scheduling."
                />
              </Grid>

              <Grid item xs={12}>
                <TagsAutocomplete
                  onSelect={(e, value) => { handleChangeTags(value); }}
                  value={tags}
                  size="default"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

GeneralFormComponent.propTypes = {
  eventTemplate: PropTypes.object,
  tags: PropTypes.array,
  handleNameChange: PropTypes.func,
  handleChangeTags: PropTypes.func,
  handleDescriptionChange: PropTypes.func,
  handleChangeEventDataDefaultValues: PropTypes.func,
  isLoading: PropTypes.bool,
};

GeneralFormComponent.defaultProps = {
  eventTemplate: {},
  tags: [],
  handleNameChange: () => {},
  handleChangeTags: () => {},
  handleDescriptionChange: () => {},
  handleChangeEventDataDefaultValues: () => {},
  isLoading: true,
};

export default GeneralFormComponent;
